import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useGlobal } from "utils/globalState";

import { Section } from "@sainsburys-tech/luna-section";
import { trackEvent } from "@jsainsburyplc/id-ui-common";

import { passwordService } from "services/password.service";
import { accountRecoveryUserData } from "constants/mfaAndOtp";
import { categoryTypes, actionTypes } from "constants/tracking";
import {
  INVALID_RESET_PASSWORD_TOKEN,
  INCORRECT_PASSWORD_CHOSEN,
} from "constants/codes";
import { scrollToTop } from "utils/scrollToTop";

import PageFormat from "components/PageFormat";
import ResetPassword from "components/ResetPassword";
import TokenExpired from "components/ResetPassword/TokenExpired";
import ResetPasswordSuccess from "components/ResetPassword/ResetPasswordSuccess";
import ResetPasswordNotificationMessage from "components/ResetPassword/ResetPasswordNotificationMessage";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const trackFormSubmit = (isAccountRecovery, action, errorCode) => {
  trackEvent({
    category: isAccountRecovery
      ? categoryTypes.ACCOUNT_RECOVERY_RESET_PASSWORD
      : categoryTypes.RESET_PASSWORD,
    label:
      action === actionTypes.ERROR
        ? `Error code: ${errorCode}`
        : "Save new password",
    action,
  });
};

const ResetPasswordPage = ({ match = {} }) => {
  const { state: locationState } = useLocation();
  const { params, viaAccountRecovery } = locationState || match || {};
  const token = (params && params.token) || undefined;

  const [isTokenValid, setIsTokenValid] = useState(undefined);
  const [isLoading, setIsLoading] = useState(undefined);
  const [userData, setUserData] = useGlobal(accountRecoveryUserData);
  const [isResetSuccess, setIsResetSuccess] = useState(undefined);

  const pageName = isResetSuccess
    ? "web:groceries:authentication:forgotten password:reset password confirmation"
    : "web:groceries:authentication:forgotten password:reset password";
  const eventName = isResetSuccess
    ? "authForgottenPasswordResetConfirmation"
    : "authForgottenPasswordReset";
  useDigitalDataTracking(
    pageName,
    "authentication",
    eventName,
    "view",
    null,
    isResetSuccess
  );

  useEffect(() => {
    const validateToken = async () => {
      try {
        setIsLoading(true);
        const response = await passwordService.checkToken(token);
        if (response.status === 200) {
          setIsTokenValid(true);
        } else {
          throw new Error();
        }
      } catch (_) {
        if (token === ":axe-test") {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
      } finally {
        setIsLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const onSubmit = async ({ password }, actions) => {
    const { setSubmitting, setStatus, setValues } = actions;
    try {
      setSubmitting(true);
      const response = await passwordService.resetPassword(token, password);
      if (response.status === 200) {
        setSubmitting(false);
        setIsResetSuccess(true);
        setUserData({ ...userData, password });
        trackFormSubmit(viaAccountRecovery, actionTypes.SUCCESS);
        return;
      }

      const data = await response.json();
      if (
        response.status === 403 ||
        data.code === INCORRECT_PASSWORD_CHOSEN ||
        data.code === INVALID_RESET_PASSWORD_TOKEN
      ) {
        const errorCode = { code: data.code };
        throw errorCode;
      }
    } catch ({ code }) {
      trackFormSubmit(viaAccountRecovery, actionTypes.ERROR, code);
      setStatus({
        isError: true,
        code,
      });
      setSubmitting(false);
      setValues({ password: "", confirmPassword: "" });
      scrollToTop();
    }
  };

  return (
    <PageFormat>
      <Section>
        {!isLoading && !isTokenValid && <TokenExpired />}

        {!isLoading && isTokenValid && (
          <>
            {isResetSuccess && (
              <ResetPasswordSuccess viaAccountRecovery={viaAccountRecovery} />
            )}
            {!isResetSuccess && (
              <>
                <div className="c-hero c-hero--center">
                  <h1>Reset your password</h1>
                </div>
                <ResetPasswordNotificationMessage />
                <p>Please enter your new password below</p>
                <ResetPassword onSubmit={onSubmit} />
              </>
            )}
          </>
        )}
      </Section>
    </PageFormat>
  );
};

export default ResetPasswordPage;
